import Badge from "@/Components/Badge/Badge";
import { editOrderAtom } from "@/shared/atoms";
import useBranch from "@/shared/atoms/useBranch";
import { useModalMeta } from "@/shared/atoms/useModal";
import { useSetPosModal } from "@/shared/atoms/usePosModal";
import useSystemTrackerHook from "@/shared/hooks/useSystemTrackerHook";
import {
  DeliveryMethods,
  OrderStatus,
  PaymentMethodEnum,
  Sources,
} from "@/shared/utils/enum";
import { OrderV2 } from "@/shared/v2/queries/interfacesV2";
import { useSetAtom } from "jotai";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Tab, Tabs, TabPanel, TabList } from "react-tabs";
import useLiveOrdersHook from "../../shared/hooks/useLiveOrdersHook";
import { orderStatusses } from "../../shared/utils/constant";
import Button from "../Button/Button";
import Textarea from "../Input/Textarea";
import Map from "../Map/Map";
import Modal, { ModalProps } from "../Modal/Modal";
import Driver from "../Order/Driver";
import Client from "../Order/Client";
import Order from "../Order/Order";
import OrderStatue from "../Order/OrderStatue";
import OrderModalMap from "./OrderModalMap";
import PrintActions from "../PrintActions/PrintActions";
import useCashDroPaymentHook from "@/shared/hooks/useCashDroPaymentHook";
import { logCashDro } from "@/shared/utils/cashDro";
import { useSetPriorityModal } from "@/shared/atoms/usePriorityModal";

export interface OrderModalProps extends ModalProps {}

const Statuses = ({ children }) => (
  <div className="flex overflow-auto bg-white mt-12 gap-2 md:gap-4">
    {children}
  </div>
);

const OrderModal = ({ onExit, ...props }: OrderModalProps) => {
  const { t } = useTranslation("", { keyPrefix: "order_modal" });
  const { t: tCommon } = useTranslation("");
  const [branch] = useBranch();
  const { showTipsOnLive } = useSystemTrackerHook();
  const [meta] = useModalMeta();
  const order: OrderV2 = meta;
  const setPosModal = useSetPosModal();
  const setPriorityModal = useSetPriorityModal();
  const [statusValue, setStatusValue] = useState(
    order?.order_information?.order_status
  );
  const { update } = useLiveOrdersHook();
  const setEditOrder = useSetAtom(editOrderAtom);
  const cashDro = useCashDroPaymentHook();
  const orderId = order?.order_information?.item_id;
  const refundAmount = parseFloat(order?.order_information?.total_price) || 0;

  const isSessionAndTakeoutOrder =
    Boolean(order.order_information.order_session_id) &&
    order.order_information.delivery_method_id === DeliveryMethods.TAKEOUT;

  const isDelivery =
    order.order_information.delivery_method_id === DeliveryMethods.DELIVERY;

  const isDineIn =
    order.order_information.delivery_method_id === DeliveryMethods.DINE_IN;

  const isQr =
    order.order_information.delivery_method_id ===
    DeliveryMethods.QR_TABLE_ORDER;

  const isCash =
    order?.order_information?.payment_method_id === PaymentMethodEnum.CASH;

  const canRefundWithCashDro = cashDro.isActive && isCash && refundAmount > 0;

  const afterCancel = () => {
    if (!canRefundWithCashDro) {
      onExit();
      return;
    }
    logCashDro("cash order cancelled, asking about a refund", {
      orderId,
      refundAmount,
    });
    setPriorityModal("cashDroCancelRefund", {
      amount: refundAmount,
      orderId: orderId,
    });
  };

  const handleClick = (id: string, value: number[]) => {
    const o = {
      orderId: order.order_information.item_id,
      status: id,
      branch: order.order_information.branch_id,
    };
    return update.mutate(o, {
      onSuccess: () => {
        setStatusValue(value[1] || value[0]);
      },
    });
  };

  return (
    <Modal width={"85rem"} onExit={onExit} {...props}>
      <Statuses>
        {orderStatusses.map((order, idx) => (
          <OrderStatue
            key={order.id}
            onClick={() => handleClick(order.id, order.value)}
            active={order.value.includes(statusValue)}
            status={tCommon(`${order.translationKey}`)}
            index={idx}
          />
        ))}
      </Statuses>

      <Tabs className="">
        <TabList className="flex gap-2 md:gap-4 my-8 w-full overflow-auto">
          <Tab className="bg-grey p-4 text-center cursor-pointer rounded-lg shrink-0 md:flex-1">
            {t("client")}
          </Tab>
          <Tab className="bg-grey p-4 text-center cursor-pointer rounded-lg shrink-0 md:flex-1">
            {t("order")}
          </Tab>
          <Tab className="bg-grey p-4 text-center cursor-pointer rounded-lg shrink-0 md:flex-1">
            {t("remark")}
            {order.order_information.remarks && <Badge />}
          </Tab>
          {isQr && (
            <Tab className="bg-grey p-4 text-center cursor-pointer rounded-lg shrink-0 md:flex-1">
              {tCommon("maps.map")}
            </Tab>
          )}
          {isDelivery && (
            <Tab className="bg-grey p-4 text-center cursor-pointer rounded-lg shrink-0 md:flex-1">
              {t("route")}
            </Tab>
          )}
          {isDelivery && (
            <Tab className="bg-grey p-4 text-center cursor-pointer rounded-lg shrink-0 md:flex-2 truncate">
              {t("chooseDeliverer")}
            </Tab>
          )}
        </TabList>
        <div className="min-h-180">
          <TabPanel>
            <Client order={order} showTipsOnLive={showTipsOnLive} />
          </TabPanel>
          <TabPanel>
            <Order
              updateKitchenStatus
              showStatus
              language={1}
              updateKitchen
              orders={order}
            />
          </TabPanel>
          <TabPanel>
            <Textarea
              readOnly
              value={order.order_information.remarks}
              className="w-full"
              rows={18}
            />
          </TabPanel>
          {isQr && (
            <TabPanel>
              <OrderModalMap order={order} />
            </TabPanel>
          )}
          {isDelivery && (
            <TabPanel>
              <Map orders={[order]} routes={[order]} branch={branch} />
            </TabPanel>
          )}
          {isDelivery && (
            <TabPanel>
              <Driver
                order={order}
                initialDriver={
                  order?.order_information?.assigned_courier_id || -1
                }
              />
            </TabPanel>
          )}
        </div>
      </Tabs>
      <PrintActions
        className="mb-4"
        orderId={order?.order_information?.item_id}
        branchId={order?.order_information?.branch_id}
      />
      <div className="flex flex-col md:flex-row justify-between gap-2">
        {!isDineIn && !isSessionAndTakeoutOrder && (
          <div className="md:self-end">
            <Button
              onClick={() => {
                setPosModal(true);
                setEditOrder(order);
                onExit();
              }}
              className="w-full"
            >
              {t("edit")}
            </Button>
          </div>
        )}
        <div className="flex gap-2 flex-col md:flex-row md:flex-1 justify-end">
          {order?.order_information?.source !== Sources.THUISBEZORGD_NL && (
            <Button
              variant="danger"
              onClick={() => {
                setPriorityModal("confirmation", {
                  mutation: update,
                  mutationBody: {
                    orderId: order?.order_information?.item_id,
                    status: "canceled",
                    branch: order.order_information.branch_id,
                  },
                  onSuccess: () => afterCancel(),
                });
              }}
            >
              {t("cancel")}
            </Button>
          )}
          <Button
            isPending={update.isPending}
            isDisabled={
              order.order_information?.order_status !== OrderStatus.ARRIVED
            }
            onClick={async () => {
              update.mutateAsync(
                {
                  orderId: order?.order_information?.item_id,
                  status: "confirmed",
                  branch: order.order_information.branch_id,
                },
                {
                  onSuccess: () => onExit(),
                }
              );
            }}
          >
            {t("approve")}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default OrderModal;
